import { useEffect } from 'react';
import { Info } from 'lucide-react';

import { track } from '@/lib/analytics';
import { ArrowInline } from '@/lib/Icon';

const TOPIC_COPY: Record<string, string> = {
  orders: 'an order question',
  shipping: 'a shipping question',
  billing: 'a billing question',
  account: 'an account question',
};

/**
 * Shown under a held ("Not sent") Providers message when the AI routing
 * check thinks it's an operational question. The patient always decides:
 * it's sent to System Support, sent to their providers anyway (where the
 * Healthie triage AI still answers ops questions, as today), or edited.
 */
export function RouteNudge({
  topic,
  onAskSupport,
  onSendAnyway,
  onEdit,
}: {
  topic: string | null;
  onAskSupport: () => void;
  onSendAnyway: () => void;
  onEdit: () => void;
}) {
  useEffect(() => {
    track('messaging_route_nudge_shown', { topic });
  }, [topic]);

  return (
    <div
      role="status"
      className="mx-15 mb-10 border border-line-strong bg-page p-15 flex flex-col gap-12"
    >
      <div className="flex items-start gap-10">
        <Info size={18} strokeWidth={1.5} className="shrink-0 text-ink-secondary mt-1" />
        <div className="flex flex-col gap-4">
          <p className="text-13 leading-135 text-ink">
            This looks like {(topic && TOPIC_COPY[topic]) || 'an order or billing question'}.
          </p>
          <p className="text-12 leading-140 text-ink-secondary">
            System Support handles orders, shipping and billing and can answer
            right away. Your providers focus on your treatment.
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-6">
        <button
          type="button"
          onClick={() => {
            track('messaging_route_nudge_action', { action: 'support', topic });
            onAskSupport();
          }}
          className="w-full h-44 px-15 flex items-center justify-between bg-black text-white text-13 hover:bg-[#252525]"
        >
          <span>Send to System Support instead</span>
          <ArrowInline />
        </button>
        <button
          type="button"
          onClick={() => {
            track('messaging_route_nudge_action', { action: 'send_anyway', topic });
            onSendAnyway();
          }}
          className="w-full h-44 px-15 flex items-center border border-line text-13 text-ink-secondary hover:text-ink"
        >
          Send to my providers anyway
        </button>
        <button
          type="button"
          onClick={() => {
            track('messaging_route_nudge_action', { action: 'edit', topic });
            onEdit();
          }}
          className="self-start h-32 text-12 text-ink-muted underline underline-offset-4 hover:text-ink"
        >
          Edit message
        </button>
      </div>
    </div>
  );
}
